/**
 * gucPlainSummary — the priced result, said in words. ONE generator.
 *
 * Spec §3.2 and §4 both reach for this and neither may fork it: the lean page's
 * verdict line, the Explain-this-result dialog's lead, and the
 * `sizerPricingSummary` that rides along on Create loan are the same sentences.
 * A second implementation would let the page and the deal's first comment
 * disagree about what happened, which is the one thing this must not do.
 *
 * RENDER-ONLY, like the rest of the response kit: every number below is read off
 * `POST /pricing/scenario/rtl`. Nothing here decides eligibility, a maximum or a
 * reduction — it only chooses the words.
 */
import { fmtMoney, fmtPct, fmtRateDec } from "../sizersShared/sizerFormat";
import { isInfoFinding } from "./gucConstants";
import { rtlRequiredCoupon } from "../sizersShared/rtlRateDisplay";

/** The metrics that breached a limit, each said as a sentence. */
export function breachLines(result) {
  return (result?.leverage || [])
    .filter((m) => m.over && m.maximum != null)
    .map(
      (m) =>
        `${m.metric} of ${fmtPct(m.requested, 2)} is over the ${fmtPct(m.maximum, 2)} maximum.`,
    );
}

/** Hard findings that are not simply a restatement of an over-limit metric. */
export function blockerLines(result) {
  const metricCodes = new Set(
    (result?.leverage || []).map((m) => `${String(m.metric).toLowerCase()}_over_max`),
  );
  return (result?.guidelineExceptions || [])
    .filter((e) => e.severity === "hard" && !metricCodes.has(e.code))
    .map((e) => e.message)
    .filter(Boolean);
}

/** The info notes — never failures, and never counted as any. */
export function noteLines(result) {
  return (result?.guidelineExceptions || [])
    .filter(isInfoFinding)
    .map((e) => e.message)
    .filter(Boolean);
}

/**
 * The verdict — one line, and the count of what is wrong when something is.
 *
 * `tone` drives the colour so no caller has to decide it twice.
 */
export function verdict(result) {
  if (!result) return null;
  const breaches = breachLines(result);
  const blockers = blockerLines(result);
  const n = breaches.length + blockers.length;

  if (result.eligible !== false && n === 0) {
    return {
      tone: "good",
      headline: "Fits the guidelines as priced.",
      lines: [],
    };
  }
  return {
    tone: "bad",
    headline: `${n} limit${n === 1 ? "" : "s"} breached.`,
    lines: [...breaches, ...blockers],
  };
}

/**
 * Every sentence the surfaces share, in reading order. The dialog prints the
 * whole list; the page prints the headline and the breach lines.
 */
export function plainLines(result) {
  if (!result) return [];
  const v = verdict(result);
  const out = [v.headline, ...v.lines];

  const rate = result.rate || {};
  if (rtlRequiredCoupon(rate) != null) {
    out.push(
      `Priced at a ${fmtRateDec(rtlRequiredCoupon(rate))} coupon` +
        (result.termMonths ? ` over ${result.termMonths} months.` : "."),
    );
  }

  const req = result.requirements || {};
  if (req.requiredLiquidity != null) {
    out.push(
      req.liquidityCovered
        ? `Required liquidity of ${fmtMoney(req.requiredLiquidity)} is covered.`
        : `Required liquidity is ${fmtMoney(req.requiredLiquidity)} against ` +
          `${fmtMoney(req.liquidityResolved)} resolved — short ${fmtMoney(req.liquidityShortfall)}.`,
    );
  }

  const notes = noteLines(result);
  if (notes.length) out.push(...notes);

  if (result.termsSubjectToChange) {
    out.push(
      "Indicative — some pricing inputs are still unvalidated, so the terms can move.",
    );
  }
  return out;
}

/**
 * The same thing as one block of prose, for `sizerPricingSummary` on Create
 * loan: the deal's first comment tells the story of how it was priced.
 */
export function plainSummaryText(result, values) {
  if (!result) return "";
  const head = [
    values?.loanProductType,
    values?.loanPurpose,
    values?.termMonths ? `${values.termMonths} months` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const lines = plainLines(result);
  return [head ? `Sized as ${head}.` : null, ...lines].filter(Boolean).join(" ");
}
